<template>
  <div class="flex flex--col gap">
    <h2 class="text-16 fw-700 text--left">{{'Personal Info' | translate}}</h2>
    <form @submit.prevent="submit">
      <div class="flex flex--col gap">
        <div class="form-inputs flex flex--col">
          <div class="row">
            <div class="col-xs-12 col-sm-6">
              <input-rd
                :label="'First name' | translate"
                required="true"
                name="first_name"
                v-model="$v.first_name.$model"
                :error="getError('first_name')"
              />
            </div>
            <div class="col-xs-12 col-sm-6 mob-mt">
              <input-rd
                :label="'Last name' | translate"
                required="true"
                name="last_name"
                v-model="$v.last_name.$model"
                :error="getError('last_name')"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-xs-12 col-sm-6">
              <input-rd
                :label="'Email' | translate"
                required="true"
                name="email"
                v-model="$v.email.$model"
                :error="getError('email')"
              />
            </div>
            <div class="col-xs-12 col-sm-6 mob-mt input-wrapper">
              <label
                :for="telephone"
                class="label-input text-14 fw-500 text-left"
              >
                {{"Phone Number" | translate}}
                <span class="text-error text-14 fw-500">*</span>
              </label>
              <vue-tel-input :class="{ 'booking-form__field--has-error': isTelephoneInvalid }"
                  v-model="$v.telephone.$model"
                  name="telephone"
                  id="telephone"
                  placeholder=""
                  input-classes="booking-form__input"
                  default-country="ch"
                  :preferred-countries="['ch', 'de']"
                  ref="telInput"
               >
              </vue-tel-input>
              <span class="text-error text-14 fw-300" v-if="getError('telephone')">{{getError('telephone') | translate}}</span>
            </div>
          </div>
          <div v-if="paymentOptions.length > 0" class="row">
            <div class="col-xs-12 col-sm-12">
              <select-rd
                :label="'Payment Method' | translate"
                required="true"
                :placeholder="'Select' | translate"
                name="paymentMethod"
                :options="paymentOptions"
                v-model="paymentMethod"
                :error="getError('paymentMethod')"
              />
            </div>
          </div>
          <text-area-rd
            :label="'Personal Message' | translate"
            v-model="message"
          />
        </div>
        <div class="flex text--left" style="gap: 10px">
          <checkbox-rd v-model="isAgree" name="agree" :isDark="isNewDesign" />
          <span :class="{'agree-message-dark': isNewDesign }" v-html="agreeMessageHTML"></span>
        </div>
      </div>
      <div class="footer">
        <div class="flex flex--v-bottom mobile-center">
          <div v-if="errorMessage.length > 0" class="field_has_error">{{ errorMessage | translate }}</div>
          <button-rd :disabled="!isAgree" :variant="isNewDesign ? 'dark' :'primary'">{{'Confirm reservation' | translate}}</button-rd>
          <button-rd @click.native="closeModal" type="button" class="cancel-btn" variant="default">{{'Cancel' | translate}}</button-rd>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { email, required } from "vuelidate/lib/validators";
import { phoneRegexp} from "../../constants/customerInfo";
import VueTelInput from '../vue-tel-input/VueTelInput.vue';

const PAYMENT_METHODS_MAP = {
  credit_card: "Online payment",
  local: "Local",
  invoice: "Invoice"
}

const ERROR_MESSAGE = {
  required: 'This field is required.',
  email: 'Invalid email',
  atLeastTwoWords: 'Should have at least 2 words',
  isValidPhone: 'Phone is invalid',
  invalidDateFormat: 'Invalid date format',
}

const atLeastTwoWords = value => {
  const words = value.trim().split(/\s+/);
  return words.length >= 2;
}

const isValidPhone = value => phoneRegexp.test(value)

export default {
  name: 'bookingForm',
  data() {
    return {
      first_name: '',
      last_name: '',
      email: '',
      telephone: '',
      country: '',
      countryCode: '',
      paymentMethod: null,
      promoCode: '',
      message: '',
      isAgree: true,
    }
  },
  props: ['onSubmit', 'closeModal', 'isTelephoneInvalid', 'allowedPaymentMethods', 'errorMessage', 'isNewDesign', 'customer'],
  components: {
    VueTelInput
  },
  mounted() {
    const customer = this.customer || {};
    this.first_name = customer.firstname || this.getFromLocalStorage('first_name') || '';
    this.last_name = customer.lastname || this.getFromLocalStorage('last_name') || '';
    this.email = customer.email || this.getFromLocalStorage('email') || '';
    this.telephone = customer.telephone || this.getFromLocalStorage('telephone') || '';
  },
  watch: {
    first_name(newValue) {
      this.updateLocalStorage('first_name', newValue);
    },
    last_name(newValue) {
      this.updateLocalStorage('last_name', newValue);
    },
    email(newValue) {
      this.updateLocalStorage('email', newValue);
    },
    telephone(newValue) {
      this.updateLocalStorage('telephone', newValue);
    },
  },
  methods: {
    async submit() {
      this.$v.$touch()
      if (this.paymentOptions.length === 0) {
        this.paymentMethod = this.defaultPayment;
      }
      if(!this.$v.$invalid && this.onSubmit) {
        const res = {
          ...this,
          country: this.$refs.telInput.$data.activeCountry.iso2.toLowerCase(),
          countryCode:  `+${this.$refs.telInput.$data.activeCountry.dialCode}`,
        }
        this.onSubmit(res)
      }
    },
    getError(field) {
      if (this.$v[field].$error) {
        if(!this.$v[field].required) return this.$options.filters.translate(ERROR_MESSAGE.required)

        switch (field) {
          case 'first_name':
            if(this.$v[field].first_name !== undefined || !this.$v[field].first_name) return this.$options.filters.translate(ERROR_MESSAGE.atLeastTwoWords)
            break;
          case 'last_name':
            if(this.$v[field].first_name !== undefined || !this.$v[field].first_name) return this.$options.filters.translate(ERROR_MESSAGE.atLeastTwoWords)
            break;
          case 'telephone':
            if(this.$v[field].isValidPhone !== undefined && !this.$v[field].isValidPhone) return this.$options.filters.translate(ERROR_MESSAGE.isValidPhone)
            if(this.$v[field].integer !== undefined && !this.$v[field].integer) return this.$options.filters.translate(ERROR_MESSAGE.isValidPhone)
            break;
          case 'email':
            if(this.$v[field].email !== undefined || !this.$v[field].email) return this.$options.filters.translate(ERROR_MESSAGE.email)
            break;
          case 'birthDateDay':
            if(this.$v[field].birthDateDay !== undefined || !this.$v[field].birthDateDay) return this.$options.filters.translate(ERROR_MESSAGE.invalidDateFormat)
            break;
          case 'birthDateMonth':
            if(this.$v[field].birthDateMonth !== undefined || !this.$v[field].birthDateDay) return this.$options.filters.translate(ERROR_MESSAGE.invalidDateFormat)
            break;
          case 'birthDateYear':
            if(this.$v[field].birthDateYear !== undefined || !this.$v[field].birthDateYear) return this.$options.filters.translate(ERROR_MESSAGE.invalidDateFormat)
            break;
        }
      }
      return ''
    },
    updateLocalStorage(key, value) {
      localStorage.setItem(key, value);
    },
    getFromLocalStorage(key) {
      return localStorage.getItem(key);
    },
  },
  computed: {
     paymentOptions() {
        return this.allowedPaymentMethods.map(method => {
          return {
            label: this.$options.filters.translate(PAYMENT_METHODS_MAP[method]),
            value: method,
          }
        })
     },
     defaultPayment() {
        return {
          label: this.$options.filters.translate("Local"),
          value: "empty",
        }
     },
     agreeMessageHTML() {
        return this.$options.filters.translate(
            `I agree to the <a target="_blank" href="/agb/" rel="nofollow">Terms and Conditions</a> and <a target="_blank" href="/datenschutz/" rel="nofollow">Data Confidentiality Agreement</a> <span class="text-error text-14 fw-500">*</span>`
        );
     },
  },
  validations: {
    first_name: {
      required,
    },
    last_name: {
      required,
    },
    telephone: {
      required,
      isValidPhone,
    },
    email: {
      required,
      email,
    },
    paymentMethod: {
      required,
    },
  }
}
</script>

<style>
  .text-left {
    text-align: left;
  }
  .gap {
    gap: 24px;
  }
  .form-inputs {
    gap: 16px;
  }
  .footer {
    padding: 24px 0 0;
  }
  .cancel-btn {
    display: none !important;
  }

  .agree-message-dark {
    a {
      color: #adadad;
      text-decoration: underline;

      &:hover,
      &:focus {
        text-decoration: none;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .flex--v-bottom.mobile-center {
      justify-content: center;
      flex-direction: column;
      gap: 10px;
    }
    .mob-mt {
      margin-top: 16px;
    }
    .cancel-btn {
      display: inline-block !important;
    }
  }

  .field_has_error {
    text-align: left;
    font-size: 0.9375rem;
    line-height: 1.125rem;
    color: #de546d;
  }
</style>



// WEBPACK FOOTER //
// src/components/booking-widget/BookingForm.vue